import React, { useState, useEffect, useRef, useCallback } from 'react';
import { submitOrder, fileToBase64, checkDuplicateOrderId } from '../services/api';
import { analyzeOrderScreenshot } from '../services/ocr';
import { getQuotaStatus, syncQuotaFromServer, decrementLocalQuota, formatResetTime, formatResetClock, fetchQuotaFromServer } from '../services/ocrRateLimit';
import SuccessPopup from './SuccessPopup';
import './OrderForm.css';

const OrderForm = ({ user, setActiveTab, fetchOrders, products = [] }) => {
    // ─── Mode ─────────────────────────────────────────────────────────────
    const [mode, setMode] = useState(''); // '' | 'manual' | 'ocr'

    // ─── Form fields ───────────────────────────────────────────────────────
    const [selectedBrand, setSelectedBrand] = useState('');
    const [brandName, setBrandName] = useState('');
    const [productName, setProductName] = useState('');
    const [orderId, setOrderId] = useState('');
    const [orderTotal, setOrderTotal] = useState('');
    const [fullName, setFullName] = useState(user.fullName || '');
    const [screenshot, setScreenshot] = useState(null);

    // ─── OCR state ─────────────────────────────────────────────────────────
    const [ocrFile, setOcrFile] = useState(null);
    const [ocrPreview, setOcrPreview] = useState('');
    const [ocrLoading, setOcrLoading] = useState(false);
    const [ocrError, setOcrError] = useState(null);   // { message, missingFields? }
    const [ocrSuccess, setOcrSuccess] = useState(false);
    const [ocrCountdown, setOcrCountdown] = useState(0);
    const [autofilled, setAutofilled] = useState({});  // which fields were auto-filled
    const [isDragging, setIsDragging] = useState(false);
    const ocrInputRef = useRef(null);
    const dropRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const debounceTimerRef = useRef(null); // debounce for OCR trigger

    // ─── Quota state (synced from server, keyed per buyerId) ───────────────
    const [quotaStatus, setQuotaStatus] = useState(() => getQuotaStatus(user?.buyerId || ''));

    // Refresh quota cache for UI display
    const refreshQuota = useCallback(() => {
        setQuotaStatus(getQuotaStatus(user?.buyerId || ''));
    }, [user]);

    // Centralize quota sync from server (PC & Mobile consistency)
    useEffect(() => {
        if (!user?.buyerId) return;
        const syncOnMount = async () => {
            const updated = await fetchQuotaFromServer(user.buyerId);
            if (updated) {
                setQuotaStatus(updated);
            }
        };
        syncOnMount();
    }, [user?.buyerId]);

    // Countdown timer effect
    useEffect(() => {
        if (ocrCountdown > 0) {
            countdownTimerRef.current = setInterval(() => {
                setOcrCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(countdownTimerRef.current);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(countdownTimerRef.current);
    }, [ocrCountdown]);
    // ─── Validation ────────────────────────────────────────────────────────
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [duplicateError, setDuplicateError] = useState('');
    const [isCheckingId, setIsCheckingId] = useState(false);

    // Effect to update fullName when user prop changes
    useEffect(() => {
        if (user && user.fullName) {
            setFullName(user.fullName);
        }
    }, [user]);

    // Debounce check for duplicate Order ID
    useEffect(() => {
        const checkId = async () => {
            if (!orderId.trim()) {
                setDuplicateError('');
                return;
            }
            setIsCheckingId(true);
            try {
                const response = await checkDuplicateOrderId(orderId.trim());
                if (response.success && response.data.exists) {
                    setDuplicateError('This Order ID already exists');
                } else {
                    setDuplicateError('');
                }
            } catch (err) {
                console.error('Error checking order ID:', err);
            } finally {
                setIsCheckingId(false);
            }
        };

        const timeoutId = setTimeout(() => {
            if (orderId) checkId();
            else setDuplicateError('');
        }, 800);

        return () => clearTimeout(timeoutId);
    }, [orderId]);

    // ─── Product/brand grouping ────────────────────────────────────────────
    const groupedProducts = products.reduce((groups, product) => {
        const brand = product.brandName || 'Other';
        if (!groups[brand]) groups[brand] = [];
        groups[brand].push(product);
        return groups;
    }, {});

    const brandList = Object.keys(groupedProducts).sort();
    const productsForBrand = selectedBrand ? groupedProducts[selectedBrand] || [] : [];

    const handleBrandChange = (e) => {
        const brand = e.target.value;
        setSelectedBrand(brand);
        setBrandName(brand);
        setProductName('');
    };

    const handleProductChange = (e) => {
        setProductName(e.target.value);
    };

    // ─── Manual screenshot upload ─────────────────────────────────────────
    const handleScreenshotChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { setError('Please upload an image file'); return; }
        if (file.size > 5 * 1024 * 1024) { setError('Image size must be less than 5MB'); return; }
        setScreenshot(file);
        setError('');
    };

    // ─── OCR: file handling ────────────────────────────────────────────────
    // Keep a stable ref to brandList and products so processOcrFile doesn't need to be recreated on every render
    const brandListRef = useRef(brandList);
    brandListRef.current = brandList;
    const productsRef = useRef(products);
    productsRef.current = products;

    // ─── Similarity helper for fuzzy matching ────────────────────────────
    const calculateSimilarity = (str1, str2) => {
        if (!str1 || !str2) return 0;
        
        // Normalize strings: lowercase, trim, remove some punctuation
        const s1 = str1.toLowerCase().trim().replace(/[|,\-]/g, ' ');
        const s2 = str2.toLowerCase().trim().replace(/[|,\-]/g, ' ');
        
        if (s1 === s2) return 1.0;
        
        // 1. Exact substring check (high weight)
        if (s1.includes(s2) || s2.includes(s1)) {
            const shorterLen = Math.min(s1.length, s2.length);
            const longerLen = Math.max(s1.length, s2.length);
            const ratio = shorterLen / longerLen;
            
            // If it's a substring and reasonably long (not just one word or 1-2 chars)
            if (shorterLen > 10) {
                // If it's a significant part of the long string (>15%), give it a strong boost
                if (ratio > 0.15) return 0.85 + (ratio * 0.15);
                // Even if very small, if it's an exact substring of a long title, it's likely a match
                return 0.75;
            }
        }

        // 2. Word-based overlap (handles cases where words are slightly different orders or have extra garbage)
        const words1 = s1.split(/\s+/).filter(w => w.length > 2); // Ignore very short words like 'a', 'to', '&'
        const words2 = s2.split(/\s+/).filter(w => w.length > 2);
        
        if (words1.length > 0 && words2.length > 0) {
            const [shorter, longer] = words1.length <= words2.length ? [words1, words2] : [words2, words1];
            const longerSet = new Set(longer);
            let matches = 0;
            for (const word of shorter) {
                if (longerSet.has(word)) matches++;
            }
            
            const wordOverlapScore = matches / shorter.length;
            if (wordOverlapScore >= 0.75) return wordOverlapScore; // Very strong word overlap
        }

        // 3. Sørensen–Dice coefficient (handles typos and spelling variations)
        const getBigrams = (str) => {
            const bigrams = new Set();
            for (let i = 0; i < str.length - 1; i++) {
                const bg = str.substring(i, i + 2);
                if (!bg.includes(' ')) bigrams.add(bg);
            }
            return bigrams;
        };

        const b1 = getBigrams(s1);
        const b2 = getBigrams(s2);
        
        if (b1.size === 0 || b2.size === 0) return 0;

        let intersection = 0;
        for (const bigram of b1) {
            if (b2.has(bigram)) intersection++;
        }

        const diceScore = (2.0 * intersection) / (b1.size + b2.size);
        return diceScore;
    };

    const processOcrFile = useCallback(async (file) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setOcrError({ message: 'Please upload a valid image file (JPG, PNG, WEBP).' });
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setOcrError({ message: 'Image size must be less than 10MB.' });
            return;
        }

        // ── Optimistic local quota check (UI only — server enforces the real limit) ──
        const localQuota = getQuotaStatus(user?.buyerId || '');
        setQuotaStatus(localQuota);
        // Only hard-block if we have a server-confirmed zero remaining count
        // (remaining === 0 means server told us — not just a missing cache entry)
        if (!localQuota.allowed && localQuota.remaining === 0) {
            setOcrError({
                message: `You have used all ${localQuota.limit} AI auto-fills for today.`,
                isRateLimitError: true,
                resetAt: localQuota.resetAt,
                resetInMs: localQuota.resetInMs,
                resetLabel: formatResetTime(localQuota.resetInMs),
            });
            return;
        }

        setOcrFile(file);
        setOcrError(null);
        setOcrSuccess(false);
        setAutofilled({});

        // Generate preview
        const reader = new FileReader();
        reader.onload = (e) => setOcrPreview(e.target.result);
        reader.readAsDataURL(file);

        // Run OCR
        setOcrLoading(true);
        try {
            const base64 = await fileToBase64(file);
            const result = await analyzeOrderScreenshot(base64, user?.buyerId || '');

            if (result.success) {
                const { orderId: oid, productName: pname, brandName: bname, orderTotal: ototal } = result.data;
                const newAutofilled = {};

                // 1. Validate Brand Match First
                const brandMatches = brandListRef.current.map(b => ({
                    name: b,
                    score: calculateSimilarity(b, bname)
                }))
                .filter(m => m.score > 0.5) // 50% threshold as requested
                .sort((a, b) => b.score - a.score);

                const matchedBrand = brandMatches.length > 0 ? brandMatches[0].name : null;

                if (!matchedBrand) {
                    setOcrError({
                        message: `The AI detected the brand "${bname}", but it does not match any of our currently active deals. Please ensure you've uploaded a screenshot of an eligible product.`
                    });
                    setOcrLoading(false);
                    return;
                }

                // 2. Validate Product Match within that Brand
                const availableProducts = productsRef.current.filter(p => p.brandName === matchedBrand);
                const productMatches = availableProducts.map(p => ({
                    product: p,
                    score: calculateSimilarity(p.productName, pname)
                }))
                .filter(m => m.score > 0.5) // 50% threshold as requested
                .sort((a, b) => b.score - a.score);

                const matchedProduct = productMatches.length > 0 ? productMatches[0].product : null;

                if (!matchedProduct) {
                    setOcrError({
                        message: `The AI detected the product "${pname}" for brand ${matchedBrand}, but we couldn't find a matching deal in our system.`,
                    });
                    setOcrLoading(false);
                    return;
                }

                // 3. Populate Fields on Successful Match
                if (oid) { setOrderId(oid); newAutofilled.orderId = true; }
                if (ototal) { setOrderTotal(ototal); newAutofilled.orderTotal = true; }
                
                setSelectedBrand(matchedBrand);
                setBrandName(matchedBrand);
                newAutofilled.brandName = true;

                setProductName(matchedProduct.productName);
                newAutofilled.productName = true;

                setAutofilled(newAutofilled);
                setOcrSuccess(true);
                setOcrError(null);

                // Always decrement locally first — guarantees the counter updates immediately
                decrementLocalQuota(user?.buyerId || '');
                // If server returned authoritative headers, override with those
                if (result.quota && result.quota.remaining !== null) {
                    syncQuotaFromServer(user?.buyerId || '', result.quota);
                }
                // Force React to re-read the (now updated) localStorage value
                setQuotaStatus(getQuotaStatus(user?.buyerId || ''));

                // The order screenshot is used as the submission screenshot too
                setScreenshot(file);
            } else {
                if (result.error === 'RATE_LIMIT_EXCEEDED') {
                    // Server-side per-user/IP rate limit hit
                    const serverQuota = {
                        remaining: 0,
                        limit: result.limit,
                        resetAt: result.resetAt,
                    };
                    syncQuotaFromServer(user?.buyerId || '', serverQuota);
                    setQuotaStatus(getQuotaStatus(user?.buyerId || ''));
                    setOcrError({
                        message: result.message,
                        isRateLimitError: true,
                        resetAt: result.resetAt,
                        resetInMs: result.resetInMs,
                        resetLabel: result.resetLabel,
                        limitedBy: result.limitedBy,
                    });
                } else if (result.error === 'API_KEY_MISSING') {
                    setOcrError({
                        message: result.message,
                        isApiKeyError: true,
                    });
                } else if (result.error === 'QUOTA_EXCEEDED') {
                    // Gemini's own server-side quota (different from our limit)
                    setOcrError({
                        message: result.message,
                        isQuotaError: true,
                    });
                    setOcrCountdown(result.waitTime || 60);
                } else {
                    setOcrError({
                        message: result.message || 'Could not extract order details.',
                        missingFields: result.missingFields || [],
                    });
                }
            }
        } catch (err) {
            setOcrError({ message: 'Unexpected error during OCR. Please try again.' });
        } finally {
            setOcrLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounced file handler — prevents rapid re-triggers within 500ms
    const handleOcrFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => processOcrFile(file), 300);
    };

    // Drag & Drop
    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (!file) return;
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(() => processOcrFile(file), 300);
    };

    const resetOcr = () => {
        setOcrFile(null);
        setOcrPreview('');
        setOcrError(null);
        setOcrSuccess(false);
        setAutofilled({});
        setScreenshot(null);
        if (ocrInputRef.current) ocrInputRef.current.value = '';
    };

    const switchMode = (newMode) => {
        setMode(newMode);
        setError('');
        setOcrError(null);
        // Reset form fields
        setSelectedBrand('');
        setBrandName('');
        setProductName('');
        setOrderId('');
        setOrderTotal('');
        setScreenshot(null);
        setAutofilled({});
        setOcrFile(null);
        setOcrPreview('');
        setOcrSuccess(false);
        setOcrLoading(false);
        // Refresh quota whenever mode changes
        refreshQuota();
    };

    // ─── Form Submit ───────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (!fullName.trim()) { setError('Please enter your profile name'); return; }
        if (!brandName.trim()) { setError('Please select or enter brand name'); return; }
        if (!productName.trim()) { setError('Please select or enter product name'); return; }
        if (!orderId.trim()) { setError('Please enter Order ID'); return; }
        if (duplicateError) { setError('Please resolve the Order ID conflict'); return; }
        if (!orderTotal.trim() || isNaN(orderTotal) || parseFloat(orderTotal) <= 0) {
            setError('Please enter a valid order total amount'); return;
        }
        if (!screenshot) { setError('Please upload the order screenshot'); return; }

        setLoading(true);
        try {
            const base64Screenshot = await fileToBase64(screenshot);
            const selectedProduct = products.find(
                p => p.brandName === brandName.trim() && p.productName === productName.trim()
            );
            const lessAmount = selectedProduct ? (selectedProduct.lessAmount || 0) : 0;

            const response = await submitOrder({
                buyerId: user.buyerId,
                fullName: fullName.trim(),
                brandName: brandName.trim(),
                productName: productName.trim(),
                orderId: orderId.trim(),
                orderTotal: parseFloat(orderTotal),
                lessAmount,
                screenshot: base64Screenshot,
            });

            if (response.success) {
                setShowSuccessPopup(true);
                setSelectedBrand('');
                setBrandName('');
                setProductName('');
                setOrderId('');
                setOrderTotal('');
                setScreenshot(null);
                setAutofilled({});
                setOcrFile(null);
                setOcrPreview('');
                setOcrSuccess(false);
                if (document.getElementById('screenshot')) document.getElementById('screenshot').value = '';
                if (fetchOrders) await fetchOrders();
            } else {
                setError(response.message || 'Order submission failed');
            }
        } catch (err) {
            setError('An unexpected error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseSuccessPopup = () => {
        setShowSuccessPopup(false);
        if (setActiveTab) setActiveTab('orders');
    };

    // ─── Render helpers ────────────────────────────────────────────────────
    const inputClass = (field) =>
        autofilled[field] ? 'autofilled-input' : '';

    // ─── Mode Selector ─────────────────────────────────────────────────────
    if (!mode) {
        const quota = quotaStatus;
        const aiBlocked = !quota.allowed;

        return (
            <div className="order-form-container">
                <div className="order-form-card">
                    <div className="order-form-header">
                        <h2>Add Order</h2>
                        <p>How would you like to add your order?</p>
                    </div>


                    <div className="mode-selector">
                        {aiBlocked ? (
                            // Quota exhausted — show locked AI card
                            <div className="mode-card mode-card-locked" id="mode-ai-btn">
                                <div className="mode-card-icon ai-icon locked-icon">
                                    <i className="ri-lock-2-fill"></i>
                                </div>
                                <div className="mode-card-content">
                                    <h3>AI Auto-Fill <span className="badge-limit">LIMIT REACHED</span></h3>
                                    <div className="quota-exhausted-msg">
                                        <i className="ri-time-line"></i>
                                        <span>
                                            You've used all <strong>{quota.limit} AI scans</strong> for today.
                                            Your limit resets in <strong>{formatResetTime(quota.resetInMs)}</strong> (at {formatResetClock(quota.resetAt)}).
                                        </span>
                                    </div>
                                    <span className="mode-card-cta locked-cta">Use manual fill below ↓</span>
                                </div>
                            </div>
                        ) : (
                            <button className="mode-card" onClick={() => switchMode('ocr')} id="mode-ai-btn">
                                <div className="mode-card-icon ai-icon">
                                    <i className="ri-sparkling-2-fill"></i>
                                </div>
                                <div className="mode-card-content">
                                    <h3>AI Auto-Fill <span className="badge-new">NEW</span></h3>
                                    <p>Upload your order screenshot and let AI automatically extract all order details for you.</p>
                                    <span className="mode-card-cta">Upload Screenshot →</span>
                                </div>
                            </button>
                        )}

                        <button className="mode-card" onClick={() => switchMode('manual')} id="mode-manual-btn">
                            <div className="mode-card-icon manual-icon">
                                <i className="ri-edit-2-fill"></i>
                            </div>
                            <div className="mode-card-content">
                                <h3>Fill Manually</h3>
                                <p>Enter your order details manually in the form fields.</p>
                                <span className="mode-card-cta">Open Form →</span>
                            </div>
                        </button>
                    </div>
                </div>

                <SuccessPopup
                    isOpen={showSuccessPopup}
                    onClose={handleCloseSuccessPopup}
                    title="Order Submitted!"
                    message="Your order has been recorded successfully and you can view it in the My Orders tab."
                />
            </div>
        );
    }

    // ─── OCR Upload Zone ───────────────────────────────────────────────────
    const renderOcrZone = () => {
        const quota = quotaStatus;
        const aiBlocked = !quota.allowed;

        return (
        <div className="ocr-zone">
            <div className="ocr-zone-header">
                <h3><i className="ri-sparkling-2-fill"></i> AI Order Scanner</h3>
                <p>Upload your order confirmation screenshot and AI will extract the details automatically.</p>
                {/* Inline quota counter */}
                <div className={`ocr-quota-inline ${aiBlocked ? 'quota-danger' : quota.remaining <= 3 ? 'quota-warning' : 'quota-ok'}`}>
                    <i className="ri-sparkling-2-fill"></i>
                    {aiBlocked
                        ? <span>Limit reached · Resets in <strong>{formatResetTime(quota.resetInMs)}</strong> at {formatResetClock(quota.resetAt)}</span>
                        : <span><strong>{quota.remaining}</strong> of {quota.limit} AI scans remaining today</span>
                    }
                </div>
            </div>

            {/* Quota exhausted — blocked state */}
            {aiBlocked && (
                <div className="ocr-quota-blocked">
                    <div className="quota-blocked-icon">
                        <i className="ri-lock-2-fill"></i>
                    </div>
                    <h4>AI Auto-Fill Limit Reached</h4>
                    <p>
                        You have used all <strong>{quota.limit} AI scans</strong> for today.
                        Your limit will reset in <strong>{formatResetTime(quota.resetInMs)}</strong> at <strong>{formatResetClock(quota.resetAt)}</strong>.
                    </p>
                    <p className="quota-blocked-hint">In the meantime, you can fill in your order details manually below.</p>
                    <button type="button" className="ocr-manual-btn quota-manual-cta" onClick={() => switchMode('manual')}>
                        <i className="ri-edit-2-line"></i> Fill Manually Instead
                    </button>
                </div>
            )}

            {!aiBlocked && !ocrFile && !ocrLoading && (
                <div
                    ref={dropRef}
                    className={`ocr-drop-area ${isDragging ? 'dragging' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => ocrInputRef.current?.click()}
                    id="ocr-drop-area"
                >
                    <input
                        ref={ocrInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleOcrFileChange}
                        style={{ display: 'none' }}
                        id="ocr-file-input"
                    />
                    <div className="ocr-drop-icon">
                        <i className="ri-image-add-line"></i>
                    </div>
                    <p className="ocr-drop-text">
                        <strong>Drop your order screenshot here</strong><br />
                        or click to browse
                    </p>
                    <p className="ocr-drop-hint">Supports JPG, PNG, WEBP · Max 10MB</p>
                    <p className="ocr-drop-hint">Works with Amazon, Flipkart, Nykaa, Myntra & more</p>
                </div>
            )}

            {!aiBlocked && ocrLoading && (
                <div className="ocr-loading">
                    <div className="ocr-loading-animation">
                        <div className="ocr-scanner-line"></div>
                        {ocrPreview && <img src={ocrPreview} alt="Uploading..." className="ocr-preview-bg" />}
                    </div>
                    <div className="ocr-loading-text">
                        <div className="ocr-spinner"></div>
                        <span>AI is reading your order...</span>
                    </div>
                </div>
            )}

            {!aiBlocked && ocrPreview && !ocrLoading && ocrFile && (
                <div className="ocr-preview-container">
                    <img src={ocrPreview} alt="Order screenshot" className="ocr-preview-img" />
                    <button className="ocr-reset-btn" onClick={resetOcr} type="button">
                        <i className="ri-refresh-line"></i> Use Different Screenshot
                    </button>
                </div>
            )}

            {!aiBlocked && ocrError && !ocrLoading && (
                <div className="ocr-error-panel">
                    <div className="ocr-error-header">
                        <i className={`ri-error-warning-fill ${ocrError.isQuotaError ? 'quota-icon' : ocrError.isRateLimitError ? 'rate-limit-icon' : ''}`}></i>
                        <h4>
                            {ocrError.isQuotaError ? 'AI Server Busy'
                            : ocrError.isRateLimitError ? 'Daily Limit Reached'
                            : 'Screenshot Could Not Be Processed'}
                        </h4>
                    </div>

                    {ocrError.isRateLimitError ? (
                        <div className="ocr-error-body">
                            <p>{ocrError.message}</p>
                            <div className="ocr-timer-display rate-limit-timer">
                                <i className="ri-lock-2-fill"></i>
                                Resets in <strong>{formatResetTime(ocrError.resetInMs)}</strong>
                                {ocrError.resetAt && <> at {formatResetClock(ocrError.resetAt)}</>}
                            </div>
                            {ocrError.limitedBy === 'ip' && (
                                <p className="ocr-error-hint" style={{ marginTop: '0.5rem' }}>
                                    This limit is applied per device/network to prevent misuse.
                                </p>
                            )}
                        </div>
                    ) : ocrError.isApiKeyError ? (
                        <div className="ocr-error-body">
                            <p>{ocrError.message}</p>
                            <p className="ocr-error-hint">Add <code>REACT_APP_GEMINI_API_KEY=your_key</code> to your <code>.env</code> file and restart the app.</p>
                        </div>
                    ) : ocrError.isQuotaError ? (
                        <div className="ocr-error-body">
                            <p>{ocrError.message}</p>
                            <div className="ocr-timer-display">
                                <i className="ri-time-line"></i> Please wait <strong>{ocrCountdown}s</strong> before trying again.
                            </div>
                        </div>
                    ) : (
                        <div className="ocr-error-body">
                            <p>{ocrError.message}</p>
                            {ocrError.missingFields && ocrError.missingFields.length > 0 && (
                                <div className="ocr-missing-fields">
                                    <p className="ocr-missing-label">Missing or unreadable fields:</p>
                                    <ul>
                                        {ocrError.missingFields.map((field, i) => (
                                            <li key={i}><i className="ri-close-circle-fill"></i> {field}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="ocr-error-actions">
                        {!ocrError.isRateLimitError && (
                            <button 
                                type="button" 
                                className="ocr-retry-btn" 
                                onClick={resetOcr}
                                disabled={ocrCountdown > 0}
                                title={ocrCountdown > 0 ? `Wait ${ocrCountdown}s` : ''}
                            >
                                <i className={ocrCountdown > 0 ? 'ri-time-line' : 'ri-upload-2-line'}></i>
                                {ocrCountdown > 0 ? `Try Again in ${ocrCountdown}s` : 'Try Another Screenshot'}
                            </button>
                        )}
                        <button type="button" className="ocr-manual-btn" onClick={() => switchMode('manual')}>
                            <i className="ri-edit-2-line"></i> Fill Manually Instead
                        </button>
                    </div>
                </div>
            )}

            {ocrSuccess && !ocrLoading && (
                <div className="ocr-success-banner">
                    <i className="ri-checkbox-circle-fill"></i>
                    <div>
                        <strong>Order details extracted!</strong>
                        <span>Review the auto-filled fields below and make any corrections before submitting.</span>
                    </div>
                </div>
            )}
        </div>
        );
    };

    // ─── Full Form ─────────────────────────────────────────────────────────
    return (
        <div className="order-form-container">
            <div className="order-form-card">
                <div className="order-form-header">
                    <h2>Add Order</h2>
                    <p>Submit your order details for refund processing</p>

                    <div className="mode-toggle-bar">
                        <button
                            type="button"
                            className={`mode-toggle-btn ${mode === 'ocr' ? 'active' : ''}`}
                            onClick={() => switchMode('ocr')}
                            id="toggle-ai-btn"
                        >
                            <i className="ri-sparkling-2-fill"></i> AI Auto-Fill
                        </button>
                        <button
                            type="button"
                            className={`mode-toggle-btn ${mode === 'manual' ? 'active' : ''}`}
                            onClick={() => switchMode('manual')}
                            id="toggle-manual-btn"
                        >
                            <i className="ri-edit-2-line"></i> Fill Manually
                        </button>
                        <button
                            type="button"
                            className="mode-back-btn"
                            onClick={() => setMode('')}
                            id="toggle-back-btn"
                        >
                            ← Back
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="order-form">
                    {/* OCR Upload Zone */}
                    {mode === 'ocr' && renderOcrZone()}

                    {/* Show form fields only in manual mode, or after OCR succeeds */}
                    {(mode === 'manual' || ocrSuccess) && (
                        <>
                            {ocrSuccess && (
                                <div className="ocr-form-divider">
                                    <span><i className="ri-arrow-down-line"></i> Review & Edit Auto-Filled Details</span>
                                </div>
                            )}

                            <div className="form-group">
                                <label htmlFor="buyerId">Buyer ID</label>
                                <input
                                    type="text"
                                    id="buyerId"
                                    value={user.buyerId}
                                    disabled
                                    className="disabled-input"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="fullName">
                                    Profile Name <span className="required">*</span>
                                    {mode === 'ocr' && <span className="manual-only-badge">Manual only</span>}
                                </label>
                                <input
                                    type="text"
                                    id="fullName"
                                    value={fullName}
                                    onChange={(e) => setFullName(e.target.value)}
                                    required
                                    placeholder="Enter your profile name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="brandName">
                                    Brand Name <span className="required">*</span>
                                    {autofilled.brandName && <span className="ai-filled-badge"><i className="ri-sparkling-2-fill"></i> AI Filled</span>}
                                </label>
                                <select
                                    id="brandName"
                                    value={selectedBrand}
                                    onChange={handleBrandChange}
                                    required
                                    className={`form-select ${inputClass('brandName')}`}
                                >
                                    <option value="">-- Select Brand --</option>
                                    {brandList.map((brand) => (
                                        <option key={brand} value={brand}>{brand}</option>
                                    ))}
                                </select>
                                {autofilled.brandName && !selectedBrand && (
                                    <small className="form-hint ai-hint">
                                        AI detected brand: <strong>"{brandName}"</strong> — please select the closest match from the dropdown above.
                                    </small>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="productName">
                                    Product Name <span className="required">*</span>
                                    {autofilled.productName && <span className="ai-filled-badge"><i className="ri-sparkling-2-fill"></i> AI Filled</span>}
                                </label>
                                <select
                                    id="productName"
                                    value={productName}
                                    onChange={handleProductChange}
                                    required
                                    disabled={!selectedBrand}
                                    className={`form-select ${inputClass('productName')}`}
                                >
                                    <option value="">-- Select Product --</option>
                                    {productsForBrand.map((product, index) => (
                                        <option key={index} value={product.productName}>
                                            {product.productName}
                                        </option>
                                    ))}
                                </select>
                                {!selectedBrand && (
                                    <small className="form-hint">Please select a brand first</small>
                                )}
                            </div>

                            <div className="form-group">
                                <label htmlFor="orderId">
                                    Order ID <span className="required">*</span>
                                    {autofilled.orderId && <span className="ai-filled-badge"><i className="ri-sparkling-2-fill"></i> AI Filled</span>}
                                </label>
                                <input
                                    type="text"
                                    id="orderId"
                                    placeholder="e.g., 407-6038794-5213943"
                                    value={orderId}
                                    onChange={(e) => setOrderId(e.target.value)}
                                    disabled={loading}
                                    required
                                    className={`${duplicateError ? 'input-error' : ''} ${inputClass('orderId')}`}
                                />
                                {isCheckingId && <small className="checking-text">Checking availability...</small>}
                                {duplicateError && <small className="error-text">{duplicateError}</small>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="orderTotal">
                                    Order Total Amount (₹) <span className="required">*</span>
                                    {autofilled.orderTotal && <span className="ai-filled-badge"><i className="ri-sparkling-2-fill"></i> AI Filled</span>}
                                </label>
                                <input
                                    type="number"
                                    id="orderTotal"
                                    placeholder="e.g., 541.65"
                                    value={orderTotal}
                                    onChange={(e) => setOrderTotal(e.target.value)}
                                    disabled={loading}
                                    step="0.01"
                                    min="0"
                                    required
                                    className={inputClass('orderTotal')}
                                />
                            </div>

                            {/* Screenshot upload — shown only in manual mode; OCR mode already has the screenshot */}
                            {mode === 'manual' && (
                                <div className="form-group">
                                    <label htmlFor="screenshot">
                                        Order Screenshot <span className="required">*</span>
                                    </label>
                                    <input
                                        type="file"
                                        id="screenshot"
                                        accept="image/*"
                                        onChange={handleScreenshotChange}
                                        disabled={loading}
                                        required
                                    />
                                    <small className="form-hint">
                                        Upload a clear screenshot of your order (max 5MB)
                                    </small>
                                </div>
                            )}

                            {mode === 'ocr' && screenshot && (
                                <div className="form-group">
                                    <label>Order Screenshot</label>
                                    <div className="ocr-screenshot-attached">
                                        <i className="ri-image-2-line"></i>
                                        <span>{ocrFile?.name || 'Order screenshot attached'}</span>
                                        <i className="ri-checkbox-circle-fill attached-check"></i>
                                    </div>
                                </div>
                            )}

                            {error && <div className="error-message">{error}</div>}
                            {success && <div className="success-message">{success}</div>}

                            <button type="submit" className="btn-primary" disabled={loading} id="submit-order-btn">
                                {loading ? (
                                    <><span className="btn-spinner"></span> Submitting...</>
                                ) : (
                                    'Add Order'
                                )}
                            </button>
                        </>
                    )}
                </form>
            </div>

            <SuccessPopup
                isOpen={showSuccessPopup}
                onClose={handleCloseSuccessPopup}
                title="Order Submitted!"
                message="Your order has been recorded successfully and you can view it in the My Orders tab."
            />
        </div>
    );
};

export default OrderForm;
